import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Trash2, Flag, RotateCcw, X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtTime, fmtDateTime, fmtAge, STATUS_CLS, priorityInfo, formatPhone } from "@/lib/format";
import { useNow } from "@/hooks/useNow";
import { useIncident, useConfig } from "@/hooks/useData";
import { useFloatingCards } from "@/context/FloatingCards";
import { usePrefs } from "@/context/PrefsContext";
import { matchesHotkey } from "@/hooks/useHotkeys";
import { useAutosave } from "./useAutosave";
import { CallTypeCombo } from "./CallTypeCombo";
import { LocationField } from "./LocationField";
import { LocationContext } from "./LocationContext";
import { UnitsPanel } from "./UnitsPanel";
import { CommentEntry, CommentsTimeline } from "./CommentsTimeline";
import { FinishCard } from "./FinishCard";

function SaveState({ state }) {
  if (state === "saving") return <span className="chip border-amber-500/50 text-amber-600 dark:text-amber-300" data-testid="save-state"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>;
  if (state === "error") return <span className="chip border-red-500/50 text-red-600 dark:text-red-300" data-testid="save-state"><AlertTriangle className="h-3 w-3" /> Save failed — retrying on next change</span>;
  return <span className="chip border-emerald-500/50 text-emerald-600 dark:text-emerald-300" data-testid="save-state"><CheckCircle2 className="h-3 w-3" /> Saved</span>;
}

function WorkspaceEmpty({ drafts, onSelect, onNewCall }) {
  const { hotkeys } = usePrefs();
  return (
    <div className="flex-1 flex flex-col items-start justify-center p-8 gap-3" data-testid="workspace-empty">
      <div className="overline-label">Call workspace</div>
      <h2 className="display text-lg font-semibold">Select a call on the left, or start a new one.</h2>
      <p className="text-sm text-muted-foreground max-w-md">Press <kbd className="mono border rounded px-1">{hotkeys.new_call}</kbd> or New Call to open a Draft. The received time is captured the moment the draft opens; the incident number is assigned only when you commit.</p>
      <button data-testid="workspace-new-call-button" onClick={onNewCall} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold">New Call</button>
      {drafts?.length > 0 && (
        <div className="mt-2 w-full max-w-md" data-testid="drafts-list">
          <div className="overline-label mb-1">Your open drafts</div>
          {drafts.map((d) => (
            <button key={d.id} data-testid={`draft-row-${d.id}`} onClick={() => onSelect(d.id)} className="w-full text-left rounded-md border px-3 py-1.5 text-xs hover:bg-accent mb-1 flex items-center gap-2">
              <span className="chip border-slate-500/40">DRAFT</span><span className="mono">{fmtTime(d.received_at)}</span><span className="truncate">{d.call_type_name || "No call type"} · {d.location_raw || "no location"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CallWorkspace({ incidentId, onSelect, onDeselect, onNewCall, units, statuses, drafts, onOpenMap }) {
  const incQ = useIncident(incidentId);
  const ctQ = useConfig("call_types");
  const dispQ = useConfig("dispositions");
  const inc = incQ.data;
  const { form, update, flush, saveState } = useAutosave(inc);
  const qc = useQueryClient();
  const cards = useFloatingCards();
  const { hotkeys } = usePrefs();
  const now = useNow(1000);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [newDisp, setNewDisp] = useState("");
  const [busy, setBusy] = useState(false);

  if (!incidentId) return <section className="pane h-full" data-testid="workspace-pane"><WorkspaceEmpty drafts={drafts} onSelect={onSelect} onNewCall={onNewCall} /></section>;
  if (incQ.isError) return <section className="pane h-full p-4 text-xs text-red-500" data-testid="workspace-pane">Could not load incident. <button className="underline" onClick={onDeselect}>Close</button></section>;
  if (!inc) return <section className="pane h-full p-4 text-xs text-muted-foreground" data-testid="workspace-pane">Loading…</section>;

  const isDraft = inc.status === "draft";
  const isFinished = inc.status === "finished";
  const readOnly = isFinished;
  const callTypes = ctQ.data || [];
  const callType = callTypes.find((c) => c.id === inc.call_type_id);
  const p = priorityInfo(form.priority);

  const commit = async () => {
    setBusy(true);
    try {
      await flush();
      const { data } = await api.post(`/incidents/${inc.id}/commit`);
      qc.setQueryData(["incident", inc.id], data);
      qc.invalidateQueries({ queryKey: ["board"] });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      toast.success(`Committed as ${data.incident_number} — received ${fmtTime(data.received_at)} · now PENDING`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const discard = async () => {
    setBusy(true);
    try {
      await api.delete(`/incidents/${inc.id}`);
      qc.invalidateQueries({ queryKey: ["drafts"] });
      toast.success("Draft discarded — no incident number consumed");
      onDeselect();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setConfirmDiscard(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${inc.id}/reopen`);
      qc.setQueryData(["incident", inc.id], data);
      qc.invalidateQueries({ queryKey: ["board"] });
      toast.success(`${inc.incident_number} re-opened → PENDING`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setConfirmReopen(false); }
  };
  const changeDisposition = async () => {
    if (!newDisp) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/incidents/${inc.id}/disposition`, { disposition_id: newDisp });
      qc.setQueryData(["incident", inc.id], data);
      qc.invalidateQueries({ queryKey: ["board"] });
      toast.success(`Disposition changed to ${data.disposition_label}`);
      setNewDisp("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setPoint = async (lat, lng, source) => {
    try { const { data } = await api.post(`/incidents/${inc.id}/location-point`, { lat, lng, source }); qc.setQueryData(["incident", inc.id], data); toast.success("Incident location set"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onKeyDown = (e) => {
    if (isDraft && matchesHotkey(e, hotkeys.commit)) { e.preventDefault(); commit(); }
  };
  const dispositions = (dispQ.data || []).filter((d) => d.active !== false && (!inc.discipline || d.discipline === inc.discipline));

  return (
    <section className="pane h-full" data-testid="workspace-pane" onKeyDown={onKeyDown}>
      <div className="pane-head">
        <div className="flex items-center gap-2">
          <span className="overline-label">{isDraft ? "Call Taking — Draft" : isFinished ? "Incident Record — Read Only" : "Call Workspace"}</span>
          <span data-testid="incident-status-chip" className={`chip mono ${STATUS_CLS[inc.status]}`}>{inc.status.toUpperCase()}</span>
          {inc.officer_initiated && <span className="chip border-teal-500/50 text-teal-700 dark:text-teal-300">OFFICER-INITIATED · {inc.initiating_unit}</span>}
        </div>
        <div className="flex items-center gap-2">
          {!isFinished && <SaveState state={saveState} />}
          <button data-testid="workspace-close-button" onClick={onDeselect} className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-accent" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="px-3 pt-2 pb-2 border-b space-y-2 shrink-0">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <div className="overline-label">Incident #</div>
            <div className="display text-2xl font-semibold mono leading-none" data-testid="incident-number">{inc.incident_number || <span className="text-muted-foreground text-base">— not assigned until commit —</span>}</div>
          </div>
          <div>
            <div className="overline-label">Received</div>
            <div className="mono text-sm font-medium leading-tight" data-testid="incident-received"><span title={fmtDateTime(inc.received_at)}>{fmtTime(inc.received_at)}</span> <span className="text-muted-foreground text-xs">· {fmtAge(inc.received_at, now)} ago</span></div>
          </div>
          {isFinished && (
            <div>
              <div className="overline-label">Finished · Disposition</div>
              <div className="text-sm leading-tight"><span className="mono">{fmtTime(inc.finished_at)}</span> · <span className="font-semibold" data-testid="incident-disposition">{inc.disposition_label}</span></div>
            </div>
          )}
          <div>
            <div className="overline-label">Taken by</div>
            <div className="text-xs leading-tight" data-testid="incident-created-by">{inc.created_by_name}</div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {isDraft && (
              <>
                {confirmDiscard ? (
                  <span className="flex items-center gap-1 text-xs">Discard draft? <button data-testid="discard-confirm-button" onClick={discard} disabled={busy} className="h-7 px-2 rounded-md bg-destructive text-destructive-foreground font-semibold">Yes, discard</button><button data-testid="discard-cancel-button" onClick={() => setConfirmDiscard(false)} className="h-7 px-2 rounded-md border">No</button></span>
                ) : (
                  <button data-testid="discard-draft-button" onClick={() => setConfirmDiscard(true)} className="h-8 px-2.5 rounded-md border text-xs hover:bg-accent flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Discard</button>
                )}
                <button data-testid="commit-button" onClick={commit} disabled={busy} className="h-8 px-3 rounded-md bg-teal-500 text-white text-xs font-semibold hover:bg-teal-700 flex items-center gap-1.5 disabled:opacity-50">
                  <Send className="h-3.5 w-3.5" /> Commit / Send <kbd className="mono text-[10px] opacity-80">{hotkeys.commit}</kbd>
                </button>
              </>
            )}
            {!isDraft && !isFinished && (
              <button data-testid="finish-button" onClick={() => cards.open("finish", FinishCard, { incidentId: inc.id }, { width: 460 })} className="h-8 px-3 rounded-md border border-amber-500/60 text-amber-700 dark:text-amber-300 text-xs font-semibold hover:bg-amber-500/10 flex items-center gap-1.5"><Flag className="h-3.5 w-3.5" /> Finish / Clear…</button>
            )}
            {isFinished && (
              confirmReopen ? (
                <span className="flex items-center gap-1 text-xs">Re-open this incident? <button data-testid="reopen-confirm-button" onClick={reopen} disabled={busy} className="h-7 px-2 rounded-md bg-primary text-primary-foreground font-semibold">Yes, re-open</button><button data-testid="reopen-cancel-button" onClick={() => setConfirmReopen(false)} className="h-7 px-2 rounded-md border">No</button></span>
              ) : (
                <button data-testid="reopen-button" onClick={() => setConfirmReopen(true)} className="h-8 px-3 rounded-md border text-xs font-semibold hover:bg-accent flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Re-open…</button>
              )
            )}
          </div>
        </div>

        {isFinished && (
          <div className="flex items-center gap-2 text-xs" data-testid="change-disposition-row">
            <span className="overline-label">Change disposition</span>
            <select data-testid="change-disposition-select" value={newDisp} onChange={(e) => setNewDisp(e.target.value)} className="field-input !h-7 w-56">
              <option value="">Select…</option>
              {dispositions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <button data-testid="change-disposition-button" disabled={!newDisp || busy} onClick={changeDisposition} className="h-7 px-2 rounded-md border hover:bg-accent disabled:opacity-50">Apply (recorded in history)</button>
          </div>
        )}

        <div className="grid grid-cols-12 gap-2">
          <label className="col-span-4">
            <span className="overline-label">Call type</span>
            <div className="mt-0.5"><CallTypeCombo callTypes={callTypes} value={form.call_type_id || ""} disabled={readOnly} onChange={(v) => update("call_type_id", v, true)} /></div>
          </label>
          <label className="col-span-2">
            <span className="overline-label">Priority</span>
            <select data-testid="priority-select" value={form.priority} disabled={readOnly} onChange={(e) => update("priority", Number(e.target.value), true)} className={`field-input mt-0.5 font-semibold ${p.cls}`}>
              <option value={1}>1 · EMERGENCY</option><option value={2}>2 · URGENT</option><option value={3}>3 · ROUTINE</option>
            </select>
          </label>
          <label className="col-span-3">
            <span className="overline-label">Caller name</span>
            <input data-testid="caller-name-input" className="field-input mt-0.5" value={form.caller_name} disabled={readOnly} onChange={(e) => update("caller_name", e.target.value)} placeholder="Optional" />
          </label>
          <label className="col-span-3">
            <span className="overline-label">Caller phone</span>
            <input data-testid="caller-phone-input" className="field-input mt-0.5 mono" value={form.caller_phone} disabled={readOnly} onChange={(e) => update("caller_phone", e.target.value)} onBlur={(e) => { const f = formatPhone(e.target.value); if (f !== e.target.value) update("caller_phone", f); }} placeholder="Optional" />
          </label>
          <div className="col-span-12">
            <span className="overline-label">Location</span>
            <div className="mt-0.5">
              <LocationField value={form.location_raw} onChange={(v) => update("location_raw", v)} incident={inc} disabled={readOnly} onOpenMap={() => onOpenMap(inc.id)} onSetPoint={setPoint} />
            </div>
          </div>
        </div>
        <LocationContext context={inc.location_context} onOpenIncident={onSelect} />
        {!isDraft && <UnitsPanel incident={inc} units={units} statuses={statuses} readOnly={readOnly} />}
      </div>

      <div className="px-3 pt-2 shrink-0">
        {readOnly ? <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid="readonly-notice">Finished incidents are read-only. Re-open to add comments or units; every re-open is recorded.</div>
          : <CommentEntry incident={inc} />}
        {!readOnly && inc.officer_initiated && callType?.narrative_prompt && (
          <div className="mt-1 text-[11px] text-muted-foreground" data-testid="narrative-prompts">Prompts: {callType.narrative_prompt.split("\n").map((l) => l.replace(":", "")).join(" · ")}</div>
        )}
        <div className="flex items-center justify-between mt-2 mb-1"><span className="overline-label">Timeline — newest first</span><span className="mono text-[10px] text-muted-foreground">{inc.comments.length} entries</span></div>
      </div>
      <CommentsTimeline incident={inc} onOpenIncident={onSelect} />
    </section>
  );
}
